import { useEffect, useSyncExternalStore } from 'react'

/**
 * Reasons the desktop app must not quit-and-restart right now (F-FE-N47).
 *
 * The update banner is global (`__root.tsx`) but the risky state lives deep
 * inside feature components — `CounterInterface`'s open checkout, for one. This
 * module is the decoupling layer: producers register a human-readable reason
 * while they're in a fragile state, the banner reads the first active one and
 * disables its action.
 *
 * Deliberately NOT a blanket "block whenever a cart has items". The counter
 * draft already survives a restart (`useCounterDraft` autosaves to localStorage
 * and flushes on `beforeunload`), so blocking on it would stall updates almost
 * permanently — which is the bug this feature exists to fix. Only register a
 * reason for state that genuinely does not survive a quit.
 *
 * The core is plain module state with subscribe/getSnapshot so it can be unit
 * tested in a node environment (this repo has no DOM test harness); the two
 * hooks are thin wrappers over it.
 */

/** Refcounted so two producers holding the same reason don't release each other. */
const reasons = new Map<string, number>()
const listeners = new Set<() => void>()

function emit(): void {
  for (const l of listeners) l()
}

/**
 * Register a reason the app must not restart. Returns a release function that
 * is safe to call more than once.
 */
export function blockRestart(reason: string): () => void {
  reasons.set(reason, (reasons.get(reason) ?? 0) + 1)
  emit()

  let released = false
  return () => {
    if (released) return
    released = true
    const next = (reasons.get(reason) ?? 1) - 1
    if (next <= 0) reasons.delete(reason)
    else reasons.set(reason, next)
    emit()
  }
}

/** The first active reason (insertion-ordered), or null when a restart is safe. */
export function getRestartBlocker(): string | null {
  const first = reasons.keys().next()
  return first.done ? null : first.value
}

export function subscribeRestartBlocker(onChange: () => void): () => void {
  listeners.add(onChange)
  return () => {
    listeners.delete(onChange)
  }
}

/** Test-only. Clears all reasons and listeners so cases can't leak into each other. */
export function __resetRestartGuard(): void {
  reasons.clear()
  listeners.clear()
}

/**
 * Hold `reason` for as long as `active` is true. The reason string is shown to
 * staff verbatim, so phrase it as an instruction ("Finish the open checkout
 * first"), not as an internal state name.
 */
export function useBlockRestart(reason: string, active: boolean): void {
  useEffect(() => {
    if (!active) return
    return blockRestart(reason)
  }, [reason, active])
}

/** The first active reason, or null when a restart is safe. */
export function useRestartBlocker(): string | null {
  return useSyncExternalStore(subscribeRestartBlocker, getRestartBlocker, getRestartBlocker)
}
